import Head from 'next/head';
import { ParsedUrlQuery } from 'querystring';

import type { WebappResponse } from '@eventbrite/event-listing-service-client';

import { useFormattedDateTimeInLocale } from '@/hooks';
import type { BasicInfo } from '@/types';
import { resolveListingCanonicalUrl, resolveListingRobotsContent } from '@/utils/seriesChildListingSeo';

import { getBaseDeepLinkAppUrl } from './getBaseDeepLinkAppUrl';
import { getFormattedEndDate, getFormattedStartDate } from '@/utils/dateTime';

const APP_ID = '487922291';
const MOBILE_CLIENT_ATTENDEE_APP = 'com.eventbrite.attendee';
const IOS_REFERRER = 'eiosurlxfbk';
const ANDROID_REFERRER = 'eandurlxfbk';
const ANDROID_BANNER_REFERRER = 'eandurlxgoog';

interface SEOTagsProps {
  basicInfo: BasicInfo;
  query: ParsedUrlQuery;
  seo: WebappResponse['seo'];
  ebHost?: string;
  locale?: string;
  /** When true, robots are always noindex (preview route; avoids conflicting with listing SEO). */
  isPreview?: boolean;
}

const generateMetaKeywords = (basicInfo: BasicInfo): string => {
  let keywords = 'Eventbrite';

  const eventName = basicInfo.name;
  const city = basicInfo.venue?.address?.city;

  if (eventName) {
    keywords += `, ${eventName}`;
    keywords += `, ${eventName} tickets`;
  }

  if (eventName && city) {
    // Only add city keywords if both name and city are present
    keywords += `, ${eventName} ${city}`;
    keywords += `, ${eventName} ${city} tickets`;
  }

  return keywords;
};

export const SEOTags = ({ basicInfo, query, seo, ebHost, locale = 'en', isPreview }: SEOTagsProps) => {
  const formattedDateTime = useFormattedDateTimeInLocale(basicInfo.startDate.local, locale);
  const latitude = basicInfo.venue?.address?.latitude;
  const longitude = basicInfo.venue?.address?.longitude;
  const venueAddress = basicInfo.venue?.address?.localizedMultiLineAddressDisplay?.join(', ');
  const baseDeepLinkAppUrl = getBaseDeepLinkAppUrl(basicInfo.id, query);

  const startDateFormatted = getFormattedStartDate(basicInfo);
  const endDateFormatted = getFormattedEndDate(basicInfo);

  const robotsContent = resolveListingRobotsContent({ basicInfo, isPreview, ebHost, seo });
  const canonicalUrl = resolveListingCanonicalUrl(basicInfo) ?? basicInfo.url;

  return (
    <Head>
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={canonicalUrl} />
      <link
        key="android-app-alternate"
        rel="alternate"
        href={`android-app://com.eventbrite.attendee/http/www.eventbrite.com/e/${basicInfo.id}?referrer=${ANDROID_BANNER_REFERRER}`}
      />

      <meta name="keywords" content={generateMetaKeywords(basicInfo)} />
      <meta property="og:determiner" content="an" />
      <meta property="og:locale" content={basicInfo.locale} />
      {latitude && longitude && (
        <>
          <meta property="event:location:latitude" content={latitude} />
          <meta property="event:location:longitude" content={longitude} />
        </>
      )}
      {startDateFormatted && <meta property="event:start_time" content={startDateFormatted} />}
      {endDateFormatted && <meta property="event:end_time" content={endDateFormatted} />}

      {/* These are deprecated by twitter but still used by Slack */}
      {venueAddress && (
        <>
          <meta name="twitter:label1" content="Where" />
          <meta name="twitter:data1" content={venueAddress} />
        </>
      )}
      {!basicInfo.hideStartDate && formattedDateTime && (
        <>
          <meta name="twitter:label2" content="When" />
          <meta name="twitter:data2" content={formattedDateTime} />
        </>
      )}

      <meta property="al:ios:app_name" content="Eventbrite" />
      <meta property="al:ios:url" content={baseDeepLinkAppUrl(IOS_REFERRER)} />
      <meta property="al:ios:app_store_id" content={APP_ID} />
      <meta property="al:android:app_name" content="Eventbrite" />
      <meta property="al:android:url" content={baseDeepLinkAppUrl(ANDROID_REFERRER)} />
      <meta property="al:android:package" content={MOBILE_CLIENT_ATTENDEE_APP} />
    </Head>
  );
};
